/**
 * ¿SON LA MISMA PLAGA? — la pregunta que decide si un tratamiento explica un
 * parte.
 *
 * El mapa daba por tratado cualquier parte que tuviera UNA pulverización en su
 * sector dentro de 21 días, mirara lo que mirara esa pulverización. En una
 * finca que pulveriza por calendario —Nave 4, sector A: trece aplicaciones en
 * siete semanas— esa ventana SIEMPRE contiene algo, así que el mapa pintaba
 * «tratado» sobre todo lo que había. De 22 partes abiertos en producción, 17
 * salían tratados y solo 2 tenían un tratamiento de verdad enlazado. Cruzando
 * la plaga, esos 17 bajan a 4.
 *
 * El dato estaba y no se leía: `spray_log_entries.pest_name_snapshot` viene
 * relleno en el 85 % de las líneas y `pest_id` en el 35 %.
 *
 * POR QUÉ NO SE PUEDEN COMPARAR LOS NOMBRES. Los dos lados escriben la misma
 * plaga distinto, y no es un descuido: el parte guarda el nombre que eligió
 * quien clasificó, y la pulverización el de la receta, que pueden venir de
 * idiomas y de catálogos distintos.
 *
 *   «Araña roja»  ·  «Two-spotted spider mite»   → Tetranychus urticae
 *   «Tuta absoluta»  ·  «Tomato Leafminer»       → Tuta absoluta
 *   «Vasates»  ·  «Tomato russet mite»           → Aculops lycopersici
 *
 * El nombre científico es el que sí coincide, y `pests.scientific_name` lo
 * tiene relleno en las 177 filas de producción. Cubre 9 de 9 nombres de parte y
 * 15 de 19 de pulverización.
 *
 * LOS QUE NO RESUELVEN NO SE INVENTAN. Cuatro nombres de pulverización no están
 * en `pests` —instantáneas huérfanas de filas borradas o de otra empresa—. Esos
 * se quedan con su propio nombre plegado como clave: dos «Western Flower
 * Thrips» siguen casando entre sí, pero ninguno casa con nada más. Peor sería
 * adivinar.
 *
 * Y SIN PLAGA NO HAY PAREJA. Una pulverización que no dice para qué era
 * devuelve `null`, y `null` no casa con nada — tampoco con otro `null`. Es
 * justo el caso que provocaba el fallo: una aplicación de calendario sin plaga
 * anotada no puede dar por tratado un parte de araña roja.
 */

export interface RawPest {
  id: string;
  name: string | null;
  scientific_name: string | null;
}

/** Minúsculas, sin acentos y con los espacios colapsados. */
export function foldPestName(raw: string | null | undefined): string {
  if (!raw) return "";
  return raw
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
}

export interface PestCanon {
  /**
   * La clave canónica de una plaga, o `null` si no se sabe cuál es.
   *
   * Dos claves iguales = la misma plaga. `null` NO es igual a `null`: quien
   * compare tiene que descartar los nulos antes, y por eso la función los
   * devuelve en vez de una cadena vacía que se colaría en un `===`.
   */
  key(pestId: string | null | undefined, name: string | null | undefined): string | null;
}

export function buildPestCanon(pests: RawPest[]): PestCanon {
  const byId = new Map<string, string>();
  const byName = new Map<string, string>();

  for (const p of pests) {
    const sci = foldPestName(p.scientific_name);
    if (!sci) continue;
    if (p.id) byId.set(p.id, sci);
    /* La MISMA plaga está repetida por empresa (177 filas, 15 empresas), así
       que el primero que llegue manda y los demás dicen lo mismo. Si alguna vez
       no lo dijeran, el que manda es estable —el orden de la consulta— y no el
       azar del recorrido. */
    const nm = foldPestName(p.name);
    if (nm && !byName.has(nm)) byName.set(nm, sci);
  }

  return {
    key(pestId, name) {
      if (pestId) {
        const bySci = byId.get(pestId);
        if (bySci) return bySci;
      }
      const folded = foldPestName(name);
      if (!folded) return null;
      /* Sin ficha en `pests`, el nombre plegado ES la clave. Casa consigo mismo
         y con nada más, que es exactamente lo que se sabe de él. */
      return byName.get(folded) ?? folded;
    },
  };
}

/** Dos plagas conocidas y coincidentes. Un `null` en cualquier lado es «no sé». */
export const samePest = (a: string | null, b: string | null): boolean =>
  a != null && b != null && a === b;
